import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.css";
import { getSentenceFromCamelCase } from "./Helpers";
import Tag from "./Tag";
import type { MSelectProps } from "../../types/form";

// If you already have MSelectProps in ../../types/form, keep that import and delete this interface.
// This local interface mirrors the common props the component needs.

const defaultLabelKey = "label";
const defaultValueKey = "value";

const MultiSelect: React.FC<MSelectProps> = ({
  className,
  disabled,
  error,
  required,
  label,
  name,
  onChange,
  style,
  data = [],
  validationHandler,
  value = [],
  size = "md",
  labelClassName,
  filter, // label key
  filterValue, // value key
  placeholder = "Select options...",
}) => {
  // --- Normalize incoming value so we always work with a string[] ---
  const normalizedValue: string[] = Array.isArray(value)
    ? value.filter((v) => typeof v === "string")
    : value
    ? [String(value)]
    : [];

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlight, setHighlight] = useState<number>(-1);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Resolve label/value keys: prefer provided filter/filterValue, else defaults
  const labelKey = filter || defaultLabelKey;
  const valueKey = filterValue || defaultValueKey;

  // Normalize options into { label, value, raw }
  const options = useMemo(
    () =>
      (data || []).map((item: any) => {
        if (typeof item === "object") {
          const lbl = item?.[labelKey] ?? item?.label ?? item?.value ?? "";
          const val = item?.[valueKey] ?? item?.value ?? "";
          return { label: String(lbl), value: String(val), raw: item };
        }
        // support string arrays
        return { label: String(item), value: String(item), raw: item };
      }),
    [data, labelKey, valueKey]
  );

  // Filtered options by search
  const visible = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, search]);

  // Close when clicking outside
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Run validation on blur of the whole widget
  const onValidationChange = () => {
    if (!validationHandler) return;
    let errorMessage = "";
    if (required && normalizedValue.length === 0) {
      errorMessage = `Please enter ${getSentenceFromCamelCase(name)}.`;
    }
    validationHandler(name, errorMessage);
  };

  const commit = (next: string[]) => onChange?.(name, next);

  const toggle = (val: string) => {
    if (disabled) return;
    const set = new Set(normalizedValue);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    commit(Array.from(set));
  };

  const removeHandler = (val: string) => {
    commit(normalizedValue.filter((v) => v !== val));
  };

  // Keyboard navigation for dropdown
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      !open &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
      return;
    }
    if (!open) return;

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(Math.max(visible.length - 1, 0), h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const target = visible[highlight];
      if (target) toggle(target.value);
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`${styles.MSInputContainer} ${styles[size]} ${
        disabled ? styles.disabled : ""
      }`}
      onBlur={onValidationChange}
      aria-disabled={disabled}
    >
      {label && (
        <label htmlFor={name} className={labelClassName}>
          {label}
          {required && <span className={styles.required}>&nbsp;*</span>}
        </label>
      )}

      {error && (
        <span className={`${styles.required} ${styles.textSmall}`}>
          {error}
        </span>
      )}

      {/* Trigger / display */}
      <div
        role="combobox"
        aria-expanded={open}
        aria-controls={`${name}-listbox`}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={() => !disabled && setOpen((s) => !s)}
        className={`${styles.cxt} ${styles.blackBorder} ${className || ""}`}
        style={style}
      >
        <div className={styles.cxtFlex}>
          {normalizedValue.length === 0 ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : (
            normalizedValue.map((val) => {
              const opt = options.find((o) => o.value === val);
              return (
                <Tag
                  key={val}
                  item={opt?.label ?? val}
                  remove={removeHandler}
                  value={val}
                />
              );
            })
          )}
        </div>
        <span className={styles.chevron} aria-hidden>
          ▾
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={styles.dropdownContainer}
          role="listbox"
          id={`${name}-listbox`}
        >
          <div className={styles.searchContainer}>
            <input
              placeholder="Search for items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="search"
              aria-label="Search options"
            />
          </div>

          <div className={styles.options}>
            {visible.map((opt, i) => {
              const checked = normalizedValue.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`${styles.optionRow} ${
                    i === highlight ? styles.optionActive : ""
                  }`}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
            {visible.length === 0 && (
              <div className={styles.noResult}>No matches</div>
            )}
          </div>

          <div className={styles.actionsBar}>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => commit(options.map((o) => o.value))}
            >
              Select all
            </button>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => commit([])}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
